import { api } from '../api.ts';
import { compact, escapeHtml, html, mount, raw } from '../html.ts';
import { currentIntlLocale, DECISION_STATUS, formatDate, formatDateTime, NOTE_KIND, options, PRIORITY, t, tk, today } from '../i18n.ts';
import { canWrite, lookups } from '../state.ts';
import { openCommentThread, renderComments } from './comments.ts';
import { confirmDialog, emptyState, field, openFormDialog, optionList, select, statusBadge, textarea, toast, toastError } from '../ui.ts';

// ---------------------------------------------------------------------------- shared

function addDelete(dialog: HTMLDialogElement, title: string, message: string, remove: () => Promise<void>) {
  if (!canWrite()) return;
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'p-button p-button--ghost pma-danger-text';
  button.textContent = t('Löschen');
  button.addEventListener('click', async () => {
    dialog.close();
    if (await confirmDialog(title, message)) await remove().catch(toastError);
  });
  dialog.querySelector('.p-dialog__actions')!.prepend(button);
}

function toolbar(label: string, count: string) {
  return html`<div class="p-table-toolbar pma-toolbar">
    <span class="p-table-toolbar__status pma-muted">${count}</span>
    <div class="p-table-toolbar__actions">${canWrite() ? html`<button class="p-button p-button--primary" type="button" data-new>${label}</button>` : ''}</div>
  </div>`;
}

/** Plain text with paragraphs and line breaks, escaped. */
export function prose(text: string | null | undefined) {
  if (!text) return '';
  return raw(
    text
      .split(/\n{2,}/)
      .map((block) => `<p>${escapeHtml(block).replace(/\n/g, '<br>')}</p>`)
      .join(''),
  );
}

// ---------------------------------------------------------------------------- decisions

export async function renderDecisions(target: HTMLElement, projectId: string) {
  let items: any[] = [];
  const members = await lookups.members();
  const people = new Map(members.map((m) => [m.id, m.label]));

  async function load() {
    items = (await api.get(`/projects/${projectId}/decisions?limit=500`)).items;
    mount(
      target,
      html`${toolbar(t('Neue Entscheidung'), t('{n} Einträge · {open} offen', { n: items.length, open: items.filter((d) => d.status === 'proposed').length }))}
      ${
        items.length
          ? html`<ol class="pma-decisions">${items.map(
              (d) => html`<li class="p-card pma-decision">
                <div class="p-card__body">
                  <header class="pma-decision__header">
                    <button type="button" class="p-button p-button--link pma-left" data-edit="${d.id}"><strong>${d.title}</strong></button>
                    ${statusBadge(d.status, DECISION_STATUS)}
                  </header>
                  <p class="pma-muted pma-small">${d.decided_on ? t('Entschieden am {date}', { date: formatDate(d.decided_on) }) : t('Erfasst am {date}', { date: formatDateTime(d.created_at) })}${
                    d.decided_by_user_id ? ` · ${people.get(d.decided_by_user_id) ?? ''}` : ''
                  }</p>
                  ${d.context ? html`<div class="pma-prose pma-small"><strong>${t('Kontext')}</strong>${prose(d.context)}</div>` : ''}
                  ${d.outcome ? html`<div class="pma-prose"><strong>${t('Ergebnis')}</strong>${prose(d.outcome)}</div>` : ''}
                </div>
              </li>`,
            )}</ol>`
          : emptyState(t('Noch keine Entscheidungen'), t('Das Entscheidungsprotokoll hält fest, was warum beschlossen wurde. Gate-Entscheidungen landen hier automatisch.'))
      }`,
    );
  }

  async function edit(decision: any | null) {
    const dialog = openFormDialog({
      title: decision ? t('Entscheidung bearbeiten') : t('Neue Entscheidung'),
      wide: true,
      fields: html`${field(t('Titel'), 'title', 'text', { required: true, value: decision?.title, span: true })}
        ${select(t('Status'), 'status', options(DECISION_STATUS, decision?.status ?? 'proposed'))}
        ${field(t('Entschieden am'), 'decided_on', 'date', { value: decision?.decided_on })}
        ${select(t('Entschieden von'), 'decided_by_user_id', optionList(members, decision?.decided_by_user_id, t('Offen')), { span: true })}
        ${textarea(t('Kontext und Optionen'), 'context', { value: decision?.context, rows: 4 })}
        ${textarea(t('Ergebnis und Begründung'), 'outcome', { value: decision?.outcome, rows: 4 })}`,
      onSubmit: async (data) => {
        if (data.status === 'approved' && !data.decided_on) data.decided_on = today();
        if (decision) await api.patch(`/decisions/${decision.id}`, data);
        else await api.post(`/projects/${projectId}/decisions`, compact(data));
        toast(t('Entscheidung gespeichert'));
        await load();
      },
    });
    if (decision) {
      const thread = document.createElement('section');
      thread.className = 'pma-span-2';
      dialog.querySelector('.pma-form-grid')!.append(thread);
      void renderComments(thread, 'decision', decision.id);
      addDelete(dialog, t('Entscheidung löschen?'), t('„{title}“ wird gelöscht. Der Verlauf bleibt im Audit-Log erhalten.', { title: decision.title }), async () => {
        await api.delete(`/decisions/${decision.id}`);
        await load();
      });
    }
  }

  target.addEventListener('click', (event) => {
    const element = event.target as HTMLElement;
    if (element.closest('[data-new]')) void edit(null);
    const id = element.closest<HTMLElement>('[data-edit]')?.dataset.edit;
    const decision = items.find((d) => d.id === id);
    if (decision) canWrite() ? void edit(decision) : openCommentThread('decision', decision.id, decision.title);
  });
  await load();
}

// ---------------------------------------------------------------------------- notes

export async function renderNotes(target: HTMLElement, projectId: string) {
  let items: any[] = [];
  let selected: string | null = null;
  const assistant = await api.get<{ provider: string | null }>('/assistant').catch(() => ({ provider: null }));

  async function load() {
    items = (await api.get(`/projects/${projectId}/notes?limit=500`)).items;
    if (!selected || !items.some((n) => n.id === selected)) selected = items[0]?.id ?? null;
    draw();
  }

  function draw() {
    const note = items.find((n) => n.id === selected);
    mount(
      target,
      html`${toolbar(t('Neue Notiz'), t('{n} Notizen', { n: items.length }))}
      ${
        items.length
          ? html`<div class="pma-notes">
              <nav class="pma-notes__list" aria-label="${t('Notizen')}"><ul>${items.map(
                (n) => html`<li><button type="button" class="pma-notes__item" data-select="${n.id}" aria-current="${n.id === selected ? 'true' : 'false'}">
                  <strong>${n.title}</strong><span class="pma-muted pma-small">${NOTE_KIND[n.kind]} · ${formatDate(n.meeting_on ?? n.updated_at.slice(0, 10))}</span></button></li>`,
              )}</ul></nav>
              ${
                note
                  ? html`<article class="p-card pma-note"><div class="p-card__body">
                      <header class="pma-decision__header"><h3>${note.title}</h3>
                        ${canWrite() ? html`<div class="pma-list__meta">${
                          assistant.provider
                            ? html`<button type="button" class="p-button p-button--sm" data-propose="${note.id}">${t('Vorschläge ableiten')}</button>`
                            : ''
                        }<button type="button" class="p-button p-button--ghost p-button--sm" data-edit="${note.id}">${t('Bearbeiten')}</button></div>` : ''}</header>
                      <p class="pma-muted pma-small">${NOTE_KIND[note.kind]}${note.meeting_on ? ` ${t('am {date}', { date: formatDate(note.meeting_on) })}` : ''} · ${t('Version {n}', { n: note.revision })} · ${t('zuletzt {when}', { when: formatDateTime(note.updated_at) })}</p>
                      <div class="pma-prose">${prose(note.body) || html`<p class="pma-muted">${t('Leer.')}</p>`}</div>
                      <section data-note-comments></section>
                    </div></article>`
                  : ''
              }
            </div>`
          : emptyState(t('Noch keine Notizen'), t('Halte Besprechungen, Beobachtungen und Lessons Learned fest – jede Änderung wird versioniert.'))
      }`,
    );
    const thread = target.querySelector<HTMLElement>('[data-note-comments]');
    if (thread && note) void renderComments(thread, 'note', note.id);
  }

  async function edit(note: any | null) {
    const dialog = openFormDialog({
      title: note ? t('Notiz bearbeiten') : t('Neue Notiz'),
      wide: true,
      fields: html`${field(t('Titel'), 'title', 'text', { required: true, value: note?.title, span: true })}
        ${select(t('Art'), 'kind', options(NOTE_KIND, note?.kind ?? 'note'))}
        ${field(t('Besprechung am'), 'meeting_on', 'date', { value: note?.meeting_on })}
        ${textarea(t('Inhalt'), 'body', { value: note?.body, rows: 14 })}`,
      onSubmit: async (data) => {
        data.body = data.body ?? '';
        if (note) await api.patch(`/notes/${note.id}`, data);
        else selected = (await api.post(`/projects/${projectId}/notes`, compact(data))).id;
        toast(t('Notiz gespeichert'));
        await load();
      },
    });
    if (note) {
      addDelete(dialog, t('Notiz löschen?'), t('„{title}“ und alle Versionen werden gelöscht.', { title: note.title }), async () => {
        await api.delete(`/notes/${note.id}`);
        await load();
      });
    }
  }

  async function propose(noteId: string, button: HTMLButtonElement) {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = t('Assistent arbeitet …');
    try {
      const { items: proposals, provider } = await api.post<{ items: any[]; provider: string }>(`/notes/${noteId}/proposals`);
      const kind: Record<string, string> = { task: t('Aufgabe'), decision: t('Entscheidung'), risk: t('Risiko') };
      openFormDialog({
        title: t('Vorschläge aus der Notiz'),
        description: proposals.length
          ? t('Erzeugt von {provider}. Nichts wird angelegt, bevor du auswählst und übernimmst. Titel lassen sich anpassen.', { provider })
          : t('Der Assistent ({provider}) hat keine Aufgaben, Entscheidungen oder Risiken gefunden.', { provider }),
        wide: true,
        fields: html`${proposals.map(
          (p, i) => html`<div class="pma-proposal pma-span-2">
            <label class="p-check"><input type="checkbox" name="pick_${i}" checked> <span class="p-badge">${kind[p.type]}</span></label>
            <label class="p-field"><span class="p-visually-hidden">${t('Titel')}</span><input class="p-input" name="title_${i}" value="${p.title}" maxlength="300"></label>
            <p class="pma-muted pma-small">${[
              p.description,
              p.due_on ? t('fällig {date}', { date: formatDate(p.due_on) }) : '',
              p.priority && p.priority !== 'medium' ? `${t('Priorität')} ${PRIORITY[p.priority] ?? p.priority}` : '',
              p.type === 'risk' ? t('Wahrscheinlichkeit {p} × Auswirkung {i}', { p: p.probability, i: p.impact }) : '',
            ]
              .filter(Boolean)
              .join(' · ')}</p>
          </div>`,
        )}`,
        submitLabel: proposals.length ? t('Auswahl übernehmen') : t('Schließen'),
        onSubmit: async (data) => {
          let created = 0;
          for (const [i, p] of proposals.entries()) {
            if (data[`pick_${i}`] !== true) continue;
            const title = (data[`title_${i}`] as string | null) ?? p.title;
            if (p.type === 'task') {
              await api.post(`/projects/${projectId}/tasks`, compact({ title, description: p.description, due_on: p.due_on, priority: p.priority }));
            } else if (p.type === 'decision') {
              await api.post(`/projects/${projectId}/decisions`, compact({ title, context: p.description, status: 'proposed' }));
            } else {
              await api.post(`/projects/${projectId}/risks`, compact({ title, description: p.description, probability: p.probability, impact: p.impact }));
            }
            created += 1;
          }
          if (created) toast(t('{n} Einträge übernommen', { n: created }), t('Aufgaben, Entscheidungen und Risiken findest du in den jeweiligen Tabs.'));
        },
      });
    } catch (error) {
      toastError(error);
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = t('Vorschläge ableiten');
    }
  }

  target.addEventListener('click', (event) => {
    const element = event.target as HTMLElement;
    if (element.closest('[data-new]')) void edit(null);
    const proposeButton = element.closest<HTMLButtonElement>('[data-propose]');
    if (proposeButton) void propose(proposeButton.dataset.propose!, proposeButton);
    const pick = element.closest<HTMLElement>('[data-select]')?.dataset.select;
    if (pick) {
      selected = pick;
      draw();
      target.querySelector<HTMLElement>(`[data-select="${pick}"]`)?.focus();
    }
    const id = element.closest<HTMLElement>('[data-edit]')?.dataset.edit;
    if (id) void edit(items.find((n) => n.id === id));
  });
  await load();
}

// ---------------------------------------------------------------------------- stakeholders

const QUADRANTS = [
  { key: 'manage', label: tk('Eng einbinden'), test: (i: number, n: number) => i >= 3 && n >= 3 },
  { key: 'satisfy', label: tk('Zufriedenstellen'), test: (i: number, n: number) => i >= 3 && n < 3 },
  { key: 'inform', label: tk('Informieren'), test: (i: number, n: number) => i < 3 && n >= 3 },
  { key: 'monitor', label: tk('Beobachten'), test: (i: number, n: number) => i < 3 && n < 3 },
];

export async function renderStakeholders(target: HTMLElement, projectId: string) {
  let items: any[] = [];
  const contacts = await lookups.contacts();
  const names = new Map(contacts.map((c) => [c.id, c.label]));

  async function load() {
    items = (await api.get(`/projects/${projectId}/stakeholders`)).items;
    mount(
      target,
      html`${toolbar(t('Stakeholder hinzufügen'), t('{n} Stakeholder', { n: items.length }))}
      ${
        items.length
          ? html`<div class="pma-stakeholder-map" role="list" aria-label="${t('Stakeholder nach Einfluss und Interesse')}">${QUADRANTS.map(
              (q) => html`<section class="pma-quadrant pma-quadrant--${q.key}" role="listitem" aria-label="${t(q.label)}">
                <h3 class="pma-small">${t(q.label)}</h3>
                <ul>${items
                  .filter((s) => q.test(s.influence, s.interest))
                  .map(
                    (s) => html`<li><button type="button" class="pma-chip-button" data-edit="${s.id}">
                      ${names.get(s.contact_id) ?? t('Kontakt')}${s.raci ? html` <b>${s.raci}</b>` : ''}${s.role ? html`<span class="pma-muted"> · ${s.role}</span>` : ''}
                    </button></li>`,
                  )}</ul>
              </section>`,
            )}
            <p class="pma-stakeholder-map__axis pma-muted pma-small">${t('↑ Einfluss · Interesse →')}</p></div>`
          : emptyState(t('Noch keine Stakeholder'), contacts.length ? t('Ordne Kontakte dem Projekt zu und bewerte Einfluss und Interesse.') : t('Lege zuerst Kontakte an und ordne sie dann dem Projekt zu.'))
      }`,
    );
  }

  async function edit(stakeholder: any | null) {
    const scale = { 1: `1 · ${t('sehr gering')}`, 2: `2 · ${t('gering')}`, 3: `3 · ${t('mittel')}`, 4: `4 · ${t('hoch')}`, 5: `5 · ${t('sehr hoch')}` };
    const available = contacts.filter((c) => stakeholder?.contact_id === c.id || !items.some((s) => s.contact_id === c.id));
    const dialog = openFormDialog({
      title: stakeholder ? t('Stakeholder bearbeiten') : t('Stakeholder hinzufügen'),
      fields: html`${select(t('Kontakt'), 'contact_id', optionList(available, stakeholder?.contact_id, t('Bitte wählen')), { required: true, span: true })}
        ${field(t('Rolle im Projekt'), 'role', 'text', { value: stakeholder?.role, span: true })}
        ${select(t('Einfluss'), 'influence', options(scale, stakeholder?.influence ?? 3))}
        ${select(t('Interesse'), 'interest', options(scale, stakeholder?.interest ?? 3))}
        ${select(t('RACI'), 'raci', options({ R: `R · ${t('Durchführung')}`, A: `A · ${t('Verantwortung')}`, C: `C · ${t('Konsultiert')}`, I: `I · ${t('Informiert')}` }, stakeholder?.raci, t('Keine')))}
        ${textarea(t('Notizen'), 'notes', { value: stakeholder?.notes, rows: 3 })}`,
      onSubmit: async (data) => {
        if (stakeholder) await api.patch(`/stakeholders/${stakeholder.id}`, data);
        else await api.post(`/projects/${projectId}/stakeholders`, compact(data));
        await load();
      },
    });
    if (stakeholder) {
      addDelete(dialog, t('Stakeholder entfernen?'), t('Der Kontakt bleibt erhalten, nur die Projektzuordnung wird entfernt.'), async () => {
        await api.delete(`/stakeholders/${stakeholder.id}`);
        await load();
      });
    }
  }

  target.addEventListener('click', (event) => {
    const element = event.target as HTMLElement;
    if (element.closest('[data-new]')) void edit(null);
    const id = element.closest<HTMLElement>('[data-edit]')?.dataset.edit;
    if (id) void edit(items.find((s) => s.id === id));
  });
  await load();
}

// ---------------------------------------------------------------------------- KPIs

function sparkline(values: number[], target: number | null): string {
  if (values.length < 2) return '';
  const width = 160;
  const height = 40;
  const all = target === null ? values : [...values, target];
  const min = Math.min(...all);
  const max = Math.max(...all);
  const span = max - min || 1;
  const x = (i: number) => (i / (values.length - 1)) * (width - 4) + 2;
  const y = (v: number) => height - 4 - ((v - min) / span) * (height - 8);
  const points = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const targetLine = target === null ? '' : `<line class="pma-spark__target" x1="0" x2="${width}" y1="${y(target).toFixed(1)}" y2="${y(target).toFixed(1)}"/>`;
  return `<svg class="pma-spark" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" aria-hidden="true">${targetLine}<polyline points="${points}"/></svg>`;
}

export async function renderKpis(target: HTMLElement, projectId: string) {
  let items: any[] = [];

  async function load() {
    items = (await api.get(`/projects/${projectId}/kpis`)).items;
    const series = await Promise.all(items.map((k) => api.get(`/kpis/${k.id}/measurements`).then((r) => r.items)));
    mount(
      target,
      html`${toolbar(t('Neue Kennzahl'), t('{n} Kennzahlen', { n: items.length }))}
      ${
        items.length
          ? html`<div class="pma-kpis">${items.map((k, i) => {
              const values: any[] = series[i];
              const last = values[values.length - 1];
              const ok = last && k.target !== null ? (k.direction === 'higher' ? last.value >= k.target : last.value <= k.target) : null;
              return html`<article class="p-card pma-kpi"><div class="p-card__body">
                <header class="pma-decision__header"><h3 class="pma-small">${k.name}</h3>
                  ${canWrite() ? html`<button type="button" class="p-button p-button--ghost p-button--sm" data-edit="${k.id}" aria-label="${t('{name} bearbeiten', { name: k.name })}">…</button>` : ''}</header>
                <p class="pma-kpi__value${ok === true ? ' pma-kpi__value--ok' : ok === false ? ' pma-kpi__value--off' : ''}">
                  ${last ? `${last.value.toLocaleString(currentIntlLocale())}${k.unit ? ` ${k.unit}` : ''}` : '–'}</p>
                <p class="pma-muted pma-small">${k.target !== null ? `${t('Ziel')} ${k.direction === 'higher' ? '≥' : '≤'} ${k.target.toLocaleString(currentIntlLocale())}${k.unit ? ` ${k.unit}` : ''}` : t('Kein Zielwert')}${
                  last ? ` · ${formatDate(last.measured_on)} (${last.source})` : ''
                }</p>
                ${raw(sparkline(values.map((v) => v.value), k.target))}
                ${canWrite() ? html`<form class="pma-inline-add" data-measure="${k.id}">
                  <input class="p-input" data-p-size="sm" type="number" step="any" name="value" required aria-label="${t('Neuer Messwert für {name}', { name: k.name })}" placeholder="${t('Wert')}">
                  <input class="p-input" data-p-size="sm" type="date" name="measured_on" value="${today()}" required aria-label="${t('Messdatum')}">
                  <button class="p-button p-button--sm" type="submit">${t('Erfassen')}</button></form>` : ''}
              </div></article>`;
            })}</div>`
          : emptyState(t('Keine Kennzahlen'), t('Definiere messbare Ziele wie Erfolgsquote oder Kosten pro Fall. Werte kommen manuell oder per API.'))
      }`,
    );
  }

  async function edit(kpi: any | null) {
    const dialog = openFormDialog({
      title: kpi ? t('Kennzahl bearbeiten') : t('Neue Kennzahl'),
      fields: html`${field(t('Name'), 'name', 'text', { required: true, value: kpi?.name, span: true })}
        ${field(t('Einheit'), 'unit', 'text', { value: kpi?.unit, placeholder: t('%, EUR, Fälle') })}
        ${field(t('Zielwert'), 'target', 'number', { value: kpi?.target, attrs: 'step="any"' })}
        ${select(t('Besser ist'), 'direction', options({ higher: t('Höher'), lower: t('Niedriger') }, kpi?.direction ?? 'higher'))}`,
      onSubmit: async (data) => {
        if (kpi) await api.patch(`/kpis/${kpi.id}`, data);
        else await api.post(`/projects/${projectId}/kpis`, compact(data));
        await load();
      },
    });
    if (kpi) {
      addDelete(dialog, t('Kennzahl löschen?'), t('„{name}“ und alle Messwerte werden gelöscht.', { name: kpi.name }), async () => {
        await api.delete(`/kpis/${kpi.id}`);
        await load();
      });
    }
  }

  target.addEventListener('click', (event) => {
    const element = event.target as HTMLElement;
    if (element.closest('[data-new]')) void edit(null);
    const id = element.closest<HTMLElement>('[data-edit]')?.dataset.edit;
    if (id) void edit(items.find((k) => k.id === id));
  });
  target.addEventListener('submit', async (event) => {
    const form = (event.target as HTMLElement).closest<HTMLFormElement>('[data-measure]');
    if (!form) return;
    event.preventDefault();
    const value = Number((form.elements.namedItem('value') as HTMLInputElement).value);
    const measuredOn = (form.elements.namedItem('measured_on') as HTMLInputElement).value;
    try {
      await api.post(`/kpis/${form.dataset.measure}/measurements`, { value, measured_on: measuredOn });
      await load();
    } catch (error) {
      toastError(error);
    }
  });
  await load();
}

